/** Home - what changed, what needs you, and where each filing stands (ADR-0212 decisions 2 and 7).
 *
 * A table-first page: a strip of five tiles, the top of Action items as a short "Needs you" grid,
 * the filings as compact paths, and - for a workspace with nothing waiting and a first filing still to
 * reach - the first-run list. It replaced the v5 hero (a sentence chosen by a code, a total and a
 * five-stage spine) because a person who comes in asks "what needs me" and "is anything late", and a
 * grid answers both without a paragraph.
 *
 * **Every number resolves to something** (rule 3). The total is the inventory's own figure
 * (`/inventory`, never a sum over a register) and a button that opens the breakdown drawer; each
 * assessed scope beside it is a button to its figures. A count is a link to the list it counts.
 *
 * **One count for "Needs you"**: the tile, the bell and the first tab of Action items all read
 * `useWaitingOnMe`, so they cannot differ. A count a role is not asked is a dash, never `0`
 * (`SummaryCountsOut` serves `null`), and a filing that could not be checked says so and is not drawn
 * as a pass. **A date in a regime that is not yet law is never counted down to**: Next filing due
 * reads live filings only (ADR-0199, defect C8).
 *
 * **Every read can fail and none defaults to empty.** A failed summary, inventory or feed is a
 * `ReadFailed` with a retry above the grid; its tile is a dash. The page does not turn a failure into
 * "nothing needs you".
 *
 * What the page no longer draws, and where it went: the sentence, the spine and the role-specific
 * hero are the filing's path and the Needs-you grid; the role notes are the absence of controls; the
 * "since you were last here" strip (`HomeByRole.SinceYouWereHere`, ADR-0111) is kept as a component
 * and is not on this page.
 */

import { House } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useMe } from "../api/auth";
import type { FilingRow } from "../api/homeSummary";
import { useHomeSummary } from "../api/homeSummary";
import type { FigureCut } from "../api/queries";
import { useFiguresPaged, useInventory } from "../api/queries";
import { useWaitingOnMe, waitingText } from "../api/waiting";
import type { WaitingItem } from "../api/waiting";
import { ReadFailed } from "../components/Loaded";
import { WindowLockBanner } from "../components/WindowLockBanner";
import {
  DashboardBand,
  EmptyState,
  GridTable,
  StatusChip,
  StatusPath,
  TablePage,
  Tile,
  TileStrip,
} from "../components/ui";
import type { ChipTone, GridColumn } from "../components/ui";
import { quantity, shortDate, tonnes } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { mayEnterData } from "../shell/routeTable";
import { mayDo } from "../shell/whoCan";
import { pagingOf, WHOLE_RECORDS_NOTE } from "./drawerFigures";
import { DueNote } from "./DueNote";
import { dueStatus, overdueCount } from "./dueStatus";
import { stepsFromVerdict } from "./filingSpine";
import { dateStillMatters, isIssuedState, issuedButOwing, stateSentence, stateWords } from "./filingState";
import { useFirstRunJourney } from "./firstRunJourney";
import { filingTitle, noDueReason, readDue, stepLabel } from "./homeWords";
import { HomeChecklist } from "./HomeChecklist";
import own from "./Home.module.css";
import { NoPeriod } from "./NoPeriod";
import { stageOfVerdict } from "./readinessModel";
import { BandFailures } from "./bands/BandFailures";
import { MonthlyCard } from "./bands/MonthlyCard";
import { ScopeShareCard } from "./bands/ShareCards";
import { useEmissionsTrend } from "./bands/useEmissionsTrend";
import { monthOfBreakdown, useMonthDrawer } from "./bands/monthBreakdown";
import { useOpenMonth } from "./bands/useOpenMonth";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import { useScreen } from "./useScreen";
import { destination, dueText, typeOf } from "./waitingRows";
import type { Screen } from "./useScreen";

/** How many of what is waiting Home lists; the rest is one link away in Action items. */
const NEEDS_ROWS = 5;

function filingTone(row: FilingRow): ChipTone {
  if (!row.available || row.verdict === null) return "warn";
  // Issued and owed something again is not the finished tone (G-Wauditor-02).
  if (issuedButOwing(row.state)) return "warn";
  if (stageOfVerdict(row.verdict).passed) return "ok";
  return row.verdict === "incomplete" ? "crit" : "warn";
}

/** The whole sentence behind a compact chip, only where the chip is shorter than it. */
function stepTitle(row: FilingRow): string | undefined {
  return issuedButOwing(row.state) ? stateSentence(row.state) : undefined;
}

/** A filing as a compact path: its name, the three stations the verdict alone can name, when it is
 * due and what it needs next. The path is drawn from the verdict and never ticks a station the
 * server did not say (`stepsFromVerdict`); the filing's own five-station path is on its page. */
function FilingPaths({ rows, screen }: { rows: readonly FilingRow[]; screen: Screen }) {
  const carry = keepSearch(screen);
  const navigate = useNavigate();
  if (rows.length === 0) {
    return (
      <span data-testid="home-filings-none">
        <StatusChip tone="neutral" size="sm">
          No filing is confirmed as owed for this period
        </StatusChip>
      </span>
    );
  }
  return (
    <ul className={own.filings} data-testid="home-filings" aria-label="Filings">
      {rows.map((row) => (
        <li key={row.obligation_id} className={own.filing} data-testid="home-filing" data-step={row.next_step}>
          <Link
            to="/reports"
            search={{ ...carry, filing: row.obligation_id }}
            className={own.filingLink}
          >
            <span className={own.filingName}>{filingTitle(row)}</span>
            <span className={own.filingMeta}>{row.legal_entity_name}</span>
          </Link>
          {row.available ? (
            <StatusPath
              steps={stepsFromVerdict(row.verdict ?? undefined, row.blocking_findings ?? 0, row.state)}
              label={`${filingTitle(row)}, where it stands`}
            />
          ) : (
            <StatusChip tone="warn" size="sm">
              Could not be checked
            </StatusChip>
          )}
          <span className={own.filingDue}>
            {row.in_force_caveat ? (
              <StatusChip tone="neutral" size="sm">
                Proposed date
              </StatusChip>
            ) : (
              // A finished (plainly issued) filing has nothing left to be late for: its date, and no
              // countdown. One that owes work again is counted down to, as the tile above says.
              <DueNote
                date={row.due_date}
                days={dateStillMatters(row.state) ? row.days_left : undefined}
                none="No due date"
              />
            )}
          </span>
          {row.available ? (
            // The next step is a way in and not only a label: it opens the filing where it is taken.
            // A button and not a second anchor, so each filing keeps the one link the browser gate
            // counts ("its path is drawn once") and the chip no longer looks pressable and does nothing.
            <button
              type="button"
              className={own.stepButton}
              data-testid="home-filing-step"
              onClick={() =>
                void navigate({ to: "/reports", search: { ...carry, filing: row.obligation_id } })
              }
            >
              <StatusChip tone={filingTone(row)} size="sm" title={stepTitle(row)}>
                {stepLabel(row)}
              </StatusChip>
            </button>
          ) : (
            <StatusChip tone={filingTone(row)} size="sm" title={stepTitle(row)}>
              {stepLabel(row)}
            </StatusChip>
          )}
          {row.in_force_label ? (
            <StatusChip tone="neutral" size="sm">
              {row.in_force_label}
            </StatusChip>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** The first-run list, drawn for an Admin only and only when nothing is waiting. Its own component so
 * that the reads the plan is made from are not asked for by any other role. */
function AdminChecklist({
  screen,
  activity,
  onLeads,
}: {
  screen: Screen;
  activity: "present" | "absent" | "unknown";
  /** Whether the list has something to say (`true`) or the journey is complete (`false`), once the
   * reads have settled: the page lets the list lead only while it has, and shows its own empty state
   * when it does not. */
  onLeads: (leads: boolean) => void;
}) {
  const { plan, settled } = useFirstRunJourney(screen.hasPeriod, activity, screen.method);
  const leads = plan !== null;
  useEffect(() => {
    if (settled) onLeads(leads);
  }, [settled, leads, onLeads]);
  return plan ? <HomeChecklist plan={plan} /> : null;
}

function HomeBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const role = me?.role;
  const navigate = useNavigate();
  const [leads, setLeads] = useState<boolean | undefined>(undefined);

  const summary = useHomeSummary(screen.window, screen.method);
  const inventory = useInventory(screen.window, screen.method);
  const waiting = useWaitingOnMe();
  const trend = useEmissionsTrend(screen, screen.method, ["1", "2", "3"]);
  const openMonth = useOpenMonth(screen);
  const hasFigures =
    !inventory.error && (inventory.data?.totals_by_scope ?? []).some((card) => card.figures > 0);
  const activity = !screen.window
    ? "absent"
    : inventory.isPending || inventory.error
      ? "unknown"
      : hasFigures
        ? "present"
        : "absent";

  // ----- the breakdown drawer: the total and each scope, from the server, a page at a time.
  const key = screen.breakdown ?? "";
  const scopeKey = /^scope-([123])$/.exec(key);
  const cut: FigureCut = scopeKey ? { scope: scopeKey[1] as "1" | "2" | "3" } : {};
  const monthDrawer = useMonthDrawer({
    key: screen.breakdown,
    window: screen.window,
    method: screen.method,
    months: trend.monthly.data?.months,
    label: "Every figure",
  });
  const breakdownRead = useFiguresPaged(
    screen.breakdown && !monthOfBreakdown(screen.breakdown) ? screen.window : undefined,
    screen.method,
    cut,
  );
  const cards = inventory.data?.totals_by_scope ?? [];
  const headline = scopeKey
    ? cards.find((card) => card.scope === scopeKey[1])?.kgco2e
    : inventory.data
      ? inventory.data.total_kgco2e
      : undefined;
  const label = scopeKey ? `Scope ${scopeKey[1]}` : "Every figure";

  const counts = summary.data?.counts;
  const filings = summary.data?.filings ?? [];
  const due = readDue(filings);
  // The date shown is the next one still ahead; with none ahead, the oldest that has passed.
  const oldest = due.overdue[0];
  const shown = due.next ?? oldest;
  // **A tile is drawn only for a count the role is asked** (ADR-0132, Q-05, G-Wviewer-09). A Viewer's
  // home is the cockpit minus the verbs: "Needs you" and "To review" are a queue and a review desk, and
  // for a role with neither they were a bare dash that read as a failed read. The server serves `null`
  // for the same roles (`SummaryCountsOut`), and `whoCan.ts` holds both to the route that gates them.
  const asksForWork = role === undefined || mayDo(role, "takeWork");
  const asksToReview = role === undefined || mayDo(role, "verifyDocument");
  const asked = (n: number | null | undefined) =>
    n === null || n === undefined ? "-" : quantity(String(n));

  const total = inventory.data && hasFigures ? tonnes(inventory.data.total_kgco2e) : "-";
  const needs = (waiting.data?.items ?? []).slice(0, NEEDS_ROWS);

  const columns: GridColumn<WaitingItem>[] = [
    {
      id: "item",
      header: "Item",
      cell: (item) => {
        const to = destination(item);
        return (
          <Link
            to={to.to}
            search={{ ...keepSearch(screen), ...(to.search ?? {}) }}
            data-testid="needs-item"
            data-kind={item.kind}
          >
            {item.title}
          </Link>
        );
      },
    },
    { id: "type", header: "Type", cell: (item) => typeOf(item) },
    { id: "site", header: "Site", cell: (item) => item.facility_name ?? "" },
    {
      id: "due",
      header: "Due",
      cell: (item) => (
        <>
          {dueText(item)}
          {item.overdue ? (
            <>
              {dueText(item) ? " " : ""}
              <StatusChip tone="warn" size="sm">
                Overdue
              </StatusChip>
            </>
          ) : null}
        </>
      ),
    },
  ];

  const failures = (
    <>
      {summary.error ? (
        <div data-testid="home-hero-failed">
          <ReadFailed
            of="The summary of your filings"
            error={summary.error}
            onRetry={() => void summary.refetch()}
          />
        </div>
      ) : null}
      {inventory.error ? (
        <div role="alert" data-testid="home-total-unread" className={own.unread}>
          The total could not be read, so it is not a total of zero.{" "}
          <button type="button" onClick={() => void inventory.refetch()}>
            Try again
          </button>
        </div>
      ) : null}
    </>
  );

  // Nothing waiting on an Admin who still has a first filing to reach: the list of steps leads. Only
  // an Admin's Home asks for the reads it is made from (companies, sites, determinations, reports).
  // **A workspace with no reporting period yet is the emptiest Home there is** (ADR-0212 decision 2:
  // "Empty: the first-run checklist"), so an Admin there is led by the list whatever the feed says -
  // the feed is read for a window that does not exist, and the first step (a company, a site, a
  // period) is exactly what is missing. With a window, the list leads only while nothing waits.
  //
  // **Not once a filing has been issued** (G-Wadmin-22): a set-up list headed "First filing" beside a row
  // that says SB 253 is Issued - with its Regimes step "To do" for an old year nobody files - is two
  // answers to one question. An issued filing is the first filing reached; later set-up is on its own screens.
  const pastFirstFiling = (summary.data?.filings ?? []).some((row) => isIssuedState(row.state));
  const candidate =
    role === "admin" &&
    !pastFirstFiling &&
    (!screen.window || (!waiting.error && waiting.data && waiting.data.items.length === 0));
  const checklist = candidate ? <AdminChecklist screen={screen} activity={activity} onLeads={setLeads} /> : null;
  // **While the list leads it is the page's content** (KD, 2026-11-02): the empty Needs-you grid and
  // the "no period" panel beneath it would say the same thing three times. `leads` is `undefined`
  // until the reads behind the list have settled, and the rest of the page waits for it rather than
  // flashing an empty grid that then disappears.
  const checklistLeads = candidate && leads !== false;

  // The filings are their own read (the summary): a failed "needs you" feed does not take them away.
  // With the list leading and nothing owed, "no filing is confirmed as owed" is the list's own news.
  const filingPaths =
    screen.window && summary.data && !(checklistLeads && filings.length === 0) ? (
      <FilingPaths rows={filings} screen={screen} />
    ) : null;
  const state = checklistLeads ? undefined : !screen.window ? (
    <NoPeriod />
  ) : waiting.error ? (
    <>
      {failures}
      <div data-testid="home-needs">
        <ReadFailed
          of="What needs you"
          error={waiting.error}
          onRetry={() => void waiting.refetch()}
        />
      </div>
    </>
  ) : undefined;

  return (
    <ScreenFrame
      screen={screen}
      title="Home"
      {...(monthDrawer ?? {
        breakdownFigures: breakdownRead.figures,
        breakdownLabel: label,
        breakdownRead,
        breakdownPaging: pagingOf(breakdownRead),
        breakdownHeadline: headline,
        breakdownNote: headline !== undefined ? WHOLE_RECORDS_NOTE : undefined,
      })}
    >
      <div data-testid="home" data-variant={role ?? "unknown"} className={own.page}>
        <TablePage
          icon={House}
          title="Home"
          band={
            <DashboardBand
              label="Home dashboard"
              charts={
                screen.window
                  ? [
                      <ScopeShareCard
                        key="scopes"
                        read={inventory}
                        inventory={inventory.data}
                        onOpen={(key) => screen.openBreakdown(key)}
                      />,
                      <MonthlyCard
                        key="months"
                        scopes={cards.filter((card) => card.assessed).map((card) => card.scope as "1" | "2" | "3")}
                        bars={trend.bars}
                        reads={trend.reads}
                        onOpenMonth={openMonth}
                        unreconciled={trend.unreconciled}
                        estimates={trend.estimates}
                      />,
                    ]
                  : undefined
              }
              tiles={
                <>
                  {/* Whether the window the total is for is closed (ADR-0094): nothing for an open one. */}
                  <WindowLockBanner lock={inventory.data?.window_lock} />
                  {screen.window ? (
                    <BandFailures
                      reads={[
                        { of: "The monthly figures", read: trend.monthly },
                        { of: "The expected bills", read: trend.coverage },
                        { of: "The comparison with the prior period", read: trend.trends },
                      ]}
                    />
                  ) : null}
            <TileStrip label="Home at a glance">
              <Tile
                value={total}
                unit={total === "-" ? undefined : "tCO₂e"}
                label="Total emissions"
                testId="home-total-tile"
                onOpen={hasFigures ? () => screen.openBreakdown("all") : undefined}
                loading={inventory.isLoading}
                {...(hasFigures && trend.delta ? { delta: trend.delta } : {})}
                openTestId="home-total"
                openLabel={`Inventory: ${total} tonnes of CO2e. Show the figures behind it`}
                infoKey="home.total"
                link={
                  hasFigures ? (
                    <>
                    <span className={own.scopes}>
                      {cards.map((card) =>
                        card.assessed && card.figures > 0 ? (
                          <button
                            key={card.scope}
                            type="button"
                            className={own.scopeButton}
                            data-testid={`home-scope-${card.scope}`}
                            onClick={() => screen.openBreakdown(`scope-${card.scope}`)}
                            aria-label={`Scope ${card.scope}: ${tonnes(card.kgco2e)} tonnes. Show the figures behind it`}
                          >
                            <span>S{card.scope}</span> <span>{tonnes(card.kgco2e)}</span>
                          </button>
                        ) : (
                          <span key={card.scope} data-testid={`home-scope-${card.scope}`}>
                            S{card.scope} {card.assessed ? "none" : "not assessed"}
                          </span>
                        ),
                      )}
                    </span>
                    {/* **The total names its Scope 2 basis** (AT-20, G-P7-09). Location is the
                        default and the basis used to appear only in the address, so a total that
                        includes Scope 2 read as if it had none. The filing page names it too. Its
                        own line, not one more item in the scope row: the row's items are a repeated
                        structure the alignment sweep holds to one right edge. */}
                    {cards.some((card) => card.scope === "2" && card.assessed) ? (
                      <span className={own.basisLine} data-testid="home-basis">
                        Scope 2 {screen.method}-based
                      </span>
                    ) : null}
                    </>
                  ) : undefined
                }
              />
              {asksForWork ? (
                <Tile
                  value={waitingText(role, waiting.data?.total)}
                  loading={waiting.isLoading}
                  label="Needs you"
                  testId="home-needs-tile"
                  link={
                    // Never "Open" under a dash: a role that is asked nothing has nowhere to go.
                    waitingText(role, waiting.data?.total) === "-" ? undefined : (
                      <Link to="/approvals" search={keepSearch(screen)}>
                        Open
                      </Link>
                    )
                  }
                />
              ) : null}
              {asksToReview ? (
                <Tile
                  value={asked(counts?.documents_to_review)}
                  loading={summary.isLoading}
                  label="To review"
                  testId="home-review-tile"
                  link={
                    counts?.documents_to_review ? (
                      <Link to="/processing" search={keepSearch(screen)}>
                        Open
                      </Link>
                    ) : undefined
                  }
                />
              ) : null}
              <Tile
                value={asked(counts?.missing_source_windows)}
                loading={summary.isLoading}
                label="Missing"
                testId="home-missing-tile"
                link={
                  counts?.missing_source_windows ? (
                    // Processing is the upload roles' place. A Viewer or an Auditor is not offered it,
                    // so their link goes to the standard report of the same gaps, which they are.
                    mayEnterData(role) ? (
                      <Link to="/processing" search={{ ...keepSearch(screen), tab: "missing" }}>
                        Open
                      </Link>
                    ) : (
                      <Link to="/data-reports" search={{ ...keepSearch(screen), report: "missing-data" }}>
                        Open
                      </Link>
                    )
                  ) : undefined
                }
              />
              {/* **Overdue is said** (U3): the next date still ahead, with "in 8 days" under it and a
                  danger line "1 filing overdue" that opens the filing; or, when nothing is ahead, the
                  date that has passed, labelled as overdue with the days it is late. */}
              <Tile
                value={shown ? shortDate(shown.due_date) : "-"}
                loading={summary.isLoading}
                label={due.next ? "Next filing due" : due.overdue.length > 0 ? "Filing overdue" : "Next filing due"}
                testId="home-due-tile"
                infoKey="home.next-due"
                link={
                  shown ? (
                    <span className={own.dueLines}>
                      {due.next ? <span className={own.dueAhead}>{dueStatus(due.next.days_left).ahead}</span> : null}
                      {/* A date that still matters on an issued filing says why: it owes work again. */}
                      {issuedButOwing(shown.state) ? (
                        <span className={own.dueAhead} data-testid="home-due-state">
                          {stateWords(shown.state, { compact: true }).word}
                        </span>
                      ) : null}
                      {oldest ? (
                        <Link
                          to="/reports"
                          search={{ ...keepSearch(screen), filing: oldest.obligation_id }}
                          className={own.stepLink}
                          data-testid="home-overdue-link"
                        >
                          <StatusChip tone="crit" size="sm">
                            {due.next || due.overdue.length > 1
                              ? overdueCount(due.overdue.length)
                              : (dueStatus(oldest.days_left).overdue ?? "")}
                          </StatusChip>
                        </Link>
                      ) : null}
                    </span>
                  ) : noDueReason(filings) ? (
                    <span className={own.dueAhead} data-testid="home-due-none">
                      {noDueReason(filings)}
                    </span>
                  ) : undefined
                }
              />
            </TileStrip>
                </>
              }
            />
          }
          extra={
            <>
              {checklist}
              {/* A failed read is said wherever the page is: with the list leading there is no grid
                  to carry it. */}
              {checklistLeads ? failures : null}
              {filingPaths}
            </>
          }
          table={
            state || checklistLeads ? undefined : (
              <>
                {failures}
                <GridTable
                  caption="What needs you"
                  columns={columns}
                  rows={needs}
                  rowKey={(item) => item.item_key}
                  rowLabel={(item) => item.title}
                  loading={waiting.isLoading}
                  onRowOpen={(item) => {
                    const to = destination(item);
                    void navigate({ to: to.to, search: { ...keepSearch(screen), ...(to.search ?? {}) } });
                  }}
                  empty={<EmptyState title="Nothing needs you." compact testId="home-needs-empty" />}
                  testId="home-needs"
                />
              </>
            )
          }
          state={state}
        />
      </div>
    </ScreenFrame>
  );
}

export function HomeScreen() {
  return (
    <RequireSession>
      <HomeBody />
    </RequireSession>
  );
}
